import { useCallback, useEffect, useState } from 'react';
import {
  api, ErreurApi,
  type FicheAttestation, type ListeAttestations, type ReglagesAttestation,
  type RequisitionDepuisActe, type TitulaireResume,
} from './api';
import { Icone } from './icones';
import { horsDuPays, dimensionsIncoherentes } from '../../src/partage/controles-acte.js';
import { LIBELLE_PIECE, type PieceIdentite } from '../../src/partage/pieces-identite.js';
import {
  LIBELLE_ORIGINE, LIBELLE_ROLE_ATTESTATION, LIBELLE_STATUT_ATTESTATION,
  type OriginePropriete, type RoleAttestation, type StatutAttestation,
} from '../../src/partage/attestation.js';
// ⚠️ UNE SEULE RÉDACTION DU NIN, partagée par l'écran et par les actes
// imprimés : deux formulations divergeraient, et c'est l'imprimée qui compte.
import { libelleNin } from '../../src/partage/nin-affichage.js';

/**
 * L'ACTE COMMUNAL — l'attestation de cession (Art.3).
 *
 * ⚠️ CET ÉCRAN NE RÉCITE AUCUNE EXIGENCE. Ce qui manque pour délivrer vient de
 * la base (`fon.attestation_manques`) et suit chaque réponse du service : c'est
 * la MÊME fonction que le déclencheur de délivrance appelle. Une seconde liste
 * tenue ici divergerait au premier correctif appliqué d'un seul côté — et la
 * divergence se verrait sous sa pire forme : un acte annoncé complet que la
 * base refuse au moment où les parties sont là.
 *
 * ⚠️ ET LES VOCABULAIRES VIENNENT DE `src/partage/` : rôles, origines, pièces
 * d'identité. Un menu inventé a déjà fait échouer un enrôlement APRÈS l'émission
 * de l'invitation, sur ce dépôt, le 13/09/2026. Ici, `tests/attestation.test.ts`
 * confronte ces listes aux énumérations PostgreSQL dans les deux sens.
 */
export function Attestations({ onOuvrirParcelle }: { onOuvrirParcelle: (id: string) => void }) {
  const [reglages, setReglages] = useState<ReglagesAttestation | null>(null);
  const [liste, setListe] = useState<ListeAttestations | null>(null);
  const [fiche, setFiche] = useState<FicheAttestation | null>(null);
  const [statut, setStatut] = useState('');
  const [dresser, setDresser] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [avis, setAvis] = useState<string | null>(null);
  const [chargement, setChargement] = useState(false);

  const dire = (e: unknown) =>
    setErreur(e instanceof ErreurApi ? e.message : String((e as Error)?.message ?? e));

  const charger = useCallback(async () => {
    setChargement(true); setErreur(null);
    try {
      const q = new URLSearchParams();
      if (statut) q.set('statut', statut);
      const [r, l] = await Promise.all([api.attestations.reglages(), api.attestations.lister(q)]);
      setReglages(r); setListe(l);
    } catch (e) { dire(e); } finally { setChargement(false); }
  }, [statut]);

  useEffect(() => { void charger(); }, [charger]);

  const ouvrir = async (reference: string) => {
    setChargement(true); setErreur(null); setAvis(null);
    try { setFiche(await api.attestations.fiche(reference)); }
    catch (e) { dire(e); } finally { setChargement(false); }
  };

  const agir = async (action: () => Promise<{ message?: string }>) => {
    setChargement(true); setErreur(null); setAvis(null);
    try {
      const r = await action();
      setAvis(r.message ?? 'Fait.');
      if (fiche) setFiche(await api.attestations.fiche(fiche.attestation.reference));
      await charger();
    } catch (e) { dire(e); } finally { setChargement(false); }
  };

  if (fiche) {
    return (
      <ActeOuvert
        fiche={fiche} reglages={reglages} chargement={chargement} erreur={erreur} avis={avis}
        onFermer={() => { setFiche(null); setAvis(null); setErreur(null); }}
        onOuvrirParcelle={onOuvrirParcelle} onAgir={agir}
      />
    );
  }

  /**
   * ⚠️ L'ÉCRAN DE L'ACTE REMPLACE LA LISTE, il ne se déplie plus dessous.
   * Règle du fondateur, 21/09/2026 : **chaque action a son écran**. Un bloc
   * ouvert sous un tableau met l'acte et le filtre de statut au même rang
   * visuel — et l'agent garde sous les yeux dix lignes qui ne le concernent
   * plus pendant qu'il engage sa commune.
   */
  if (dresser) {
    return (
      <DresserActe
        onFermer={() => setDresser(false)}
        onDresser={async (corps) => {
          await agir(() => api.attestations.ouvrir(corps));
          setDresser(false);
        }}
      />
    );
  }

  return (
    <div className="tableau-bord">
      <div className="entete-scene">
        <h2><Icone nom="attestation" taille={20} /> Attestations de cession</h2>
        {/* ⚠️ LA PHRASE VIENT DU SERVICE (`reglages.avertissement`), pas d'ici.
            Elle est la seule protection contre la double vente : deux acquéreurs
            de bonne foi, chacun avec « son papier de la mairie ». */}
        <p className="mention">
          {reglages?.avertissement}
          {reglages?.autorite && <> Délivrée par {reglages.autorite.type_organisation} — {reglages.autorite.fondement}</>}
        </p>
      </div>

      <div className="filtres">
        <label>Statut
          <select value={statut} onChange={(e) => setStatut(e.target.value)}>
            <option value="">Tous</option>
            {(['brouillon', 'signee', 'delivree', 'annulee'] as StatutAttestation[]).map((s) => (
              <option key={s} value={s}>{LIBELLE_STATUT_ATTESTATION[s]}</option>
            ))}
          </select>
        </label>
        <button className="bouton primaire" onClick={() => setDresser(true)}>
          Dresser un acte
        </button>
      </div>

      {erreur && <p className="erreur">{erreur}</p>}
      {avis && <p className="succes">{avis}</p>}
      {chargement && <p className="attente">Interrogation du registre communal…</p>}

      {liste && (liste.attestations.length === 0
        ? <p className="indetermine">{liste.message}</p>
        : (
          <table className="tableau">
            <thead><tr><th>Référence</th><th>Statut</th><th>Commune</th><th>Acquéreur</th>
              <th>Terrain</th><th>Parties</th><th>À compléter</th><th>Dressé le</th></tr></thead>
            <tbody>
              {liste.attestations.map((a) => (
                <tr key={a.reference}>
                  <td><button className="lien mono" onClick={() => void ouvrir(a.reference)}>{a.reference}</button></td>
                  <td>
                    {LIBELLE_STATUT_ATTESTATION[a.statut as StatutAttestation] ?? a.statut}
                    {a.opposition_recue && <em className="mention"> · opposition</em>}
                  </td>
                  <td>{a.commune}</td>
                  <td>{a.acquereur}</td>
                  <td>{a.parcelle
                    ? <button className="lien" onClick={() => onOuvrirParcelle(a.parcelle!)}>{a.parcelle}</button>
                    : <em className="mention">tracé seul</em>}</td>
                  <td>{a.parties}</td>
                  {/* ⚠️ « 0 à compléter » et « acte délivré » ne se confondent pas :
                      la colonne dit le nombre de points restants, pas un état. */}
                  <td>{a.statut === 'delivree' ? '—' : a.manques}</td>
                  <td>{String(a.cree_le).slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ))}
      {liste && liste.rendus < liste.total && (
        <p className="mention">{liste.rendus} affiché(s) sur {liste.total} au registre communal.</p>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * DRESSER L'ACTE
 * ═══════════════════════════════════════════════════════════════════════════ */

/**
 * ⚠️ L'ANCRAGE EST OBLIGATOIRE ET IMMUABLE. Il dit OÙ est le sol attesté, et
 * c'est lui que le sceau de la référence atteste. Le déplacer après coup
 * changerait la commune compétente sans changer l'acte : la base le refuse
 * (070), et le formulaire le DIT avant que l'agent ne le découvre.
 */
/* ⚠️ Les deux contrôles ont déménagé dans `src/partage/controles-acte.ts` :
   un essai ne peut pas importer un `.tsx` (`--jsx` absent de
   `tsconfig.tests.json`), et le rouge n'apparaîtrait qu'au typecheck du
   pré-vol — c'est-à-dire au moment de déployer. */

const ETAPES = ['Le terrain', "L'acquéreur", 'Relecture'] as const;

/**
 * DRESSER UNE ATTESTATION DE CESSION — un écran, une opération.
 *
 * ⚠️ CE N'ÉTAIT PAS UN ÉCRAN, C'ÉTAIT UN BLOC DÉPLIÉ SOUS UNE LISTE. Treize
 * champs de même poids dans des rangées de filtres : l'ancrage, le NIN, le
 * lot, les côtés, le motif — rien ne disait ce qui engage et ce qui décore.
 * Règle posée par le fondateur le 21/09/2026 : **chaque action a son écran**.
 * Un acte notarié n'est pas un filtre de tableau.
 */
function DresserActe({ onDresser, onFermer }: {
  onDresser: (corps: Record<string, unknown>) => Promise<void>;
  onFermer: () => void;
}) {
  const [etape, setEtape] = useState<0 | 1 | 2>(0);
  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');
  const [parcelle, setParcelle] = useState('');
  const [acquereur, setAcquereur] = useState<TitulaireResume | null>(null);
  const [acqNin, setAcqNin] = useState('');
  const [acqNinSource, setAcqNinSource] = useState('carte_cedeao');
  const [district, setDistrict] = useState('');
  const [secteur, setSecteur] = useState('');
  const [lot, setLot] = useState('');
  const [longueur, setLongueur] = useState('');
  const [largeur, setLargeur] = useState('');
  const [superficie, setSuperficie] = useState('');
  const [motif, setMotif] = useState('');
  const [envoi, setEnvoi] = useState(false);

  const alertePoint = horsDuPays(lat, lon);
  const alerteDim = dimensionsIncoherentes(longueur, largeur, superficie);
  const terrainPret = lat.trim() !== '' && lon.trim() !== '' && !alertePoint;
  const acquereurPret = acquereur !== null;
  const ninNonVerifie = acqNin.trim() !== '' && acqNinSource === 'declaration';

  async function dresser() {
    setEnvoi(true);
    try {
      await onDresser({
        ancrage: { lat: Number(lat), lon: Number(lon) },
        identifiantParcelle: parcelle.trim() || undefined,
        acquereurTitulaireId: acquereur?.id,
        acquereurNin: acqNin.trim() || undefined,
        acquereurNinSource: acqNin.trim() ? acqNinSource : undefined,
        district: district.trim() || undefined,
        secteur: secteur.trim() || undefined,
        lotNumero: lot.trim() || undefined,
        longueurM: longueur ? Number(longueur) : undefined,
        largeurM: largeur ? Number(largeur) : undefined,
        superficieM2: superficie ? Number(superficie) : undefined,
        motif,
      });
    } finally { setEnvoi(false); }
  }

  return (
    <section className="acte-ecran">
      <header className="acte-tete">
        <div>
          <h2>Dresser une attestation de cession</h2>
          <p className="acte-sous-titre">
            L'acte est dressé AU NOM de votre commune — son autorité est celle de votre
            organisation, vérifiée par le service, jamais déclarée ici.
          </p>
        </div>
        <button type="button" className="bouton discret" onClick={onFermer}>Abandonner</button>
      </header>

      {/* ⚠️ LES ÉTAPES SE VOIENT ET SE REMONTENT, mais ne se SAUTENT pas :
          on peut revenir corriger le terrain depuis la relecture, jamais aller
          relire un acte dont le terrain est hors du pays. */}
      <ol className="acte-etapes">
        {ETAPES.map((nom, i) => (
          <li key={nom} className={i === etape ? 'active' : i < etape ? 'faite' : ''}>
            <button type="button" disabled={i > etape} onClick={() => setEtape(i as 0 | 1 | 2)}>
              <span className="rang">{i + 1}</span>{nom}
            </button>
          </li>
        ))}
      </ol>

      {etape === 0 && (
        <div className="acte-corps">
          <h3>Où se trouve le terrain</h3>
          <div className="filtres">
            <label>Latitude de l'ancrage
              <input value={lat} onChange={(e) => setLat(e.target.value)} required
                     inputMode="decimal" placeholder="9.5370" />
            </label>
            <label>Longitude de l'ancrage
              <input value={lon} onChange={(e) => setLon(e.target.value)} required
                     inputMode="decimal" placeholder="-13.6785" />
            </label>
            <label>Parcelle au registre (si elle existe)
              <input value={parcelle} onChange={(e) => setParcelle(e.target.value)} placeholder="GN-PAR-…" />
            </label>
          </div>
          {alertePoint && <p className="acte-alerte" role="alert">{alertePoint}</p>}

          <h3>Comment le terrain se désigne sur le papier</h3>
          <p className="aide">
            Mowibo Maps s'arrête à la commune : district, secteur et lot ne sont pas
            servis par le registre, ils sont recueillis auprès de l'usager.
          </p>
          <div className="filtres">
            <label>District<input value={district} onChange={(e) => setDistrict(e.target.value)} /></label>
            <label>Secteur<input value={secteur} onChange={(e) => setSecteur(e.target.value)} /></label>
            <label>Lot n°<input value={lot} onChange={(e) => setLot(e.target.value)} /></label>
          </div>
          <div className="filtres">
            <label>Longueur (m)<input value={longueur} onChange={(e) => setLongueur(e.target.value)} inputMode="decimal" /></label>
            <label>Largeur (m)<input value={largeur} onChange={(e) => setLargeur(e.target.value)} inputMode="decimal" /></label>
            <label>Superficie (m²)<input value={superficie} onChange={(e) => setSuperficie(e.target.value)} inputMode="decimal" /></label>
          </div>
          {alerteDim && <p className="acte-avis">{alerteDim}</p>}

          <div className="actions">
            <button type="button" className="bouton primaire" disabled={!terrainPret}
                    onClick={() => setEtape(1)}>Continuer</button>
            {!terrainPret && !alertePoint && <span className="aide">L'ancrage est obligatoire.</span>}
          </div>
        </div>
      )}

      {etape === 1 && (
        <div className="acte-corps">
          <ChoisirTitulaire
            libelle="Acquéreur" choisi={acquereur} onChoisir={setAcquereur}
            aide="Celui qui reçoit le terrain. Il est DÉSIGNÉ parmi les personnes connues du registre, jamais créé ici : chaque acte fabriquerait sinon un doublon de la même personne." />
          <div className="filtres">
            <label>NIN de l'acquéreur
              <input value={acqNin} inputMode="numeric" maxLength={20} placeholder="15 chiffres"
                     onChange={(e) => setAcqNin(e.target.value)} />
            </label>
            {acqNin.trim() && (
              <label>Source
                <select value={acqNinSource} onChange={(e) => setAcqNinSource(e.target.value)}>
                  <option value="carte_cedeao">Carte CEDEAO présentée — vérifié</option>
                  <option value="mowibo_id">Mowibo ID — vérifié</option>
                  <option value="declaration">Déclaré par l'intéressé — NON vérifié</option>
                </select>
              </label>
            )}
          </div>
          {/* ⚠️ LA SOURCE N'EST PAS UNE MENTION DÉCORATIVE : elle dit si
              l'identité a été CONSTATÉE ou seulement RAPPORTÉE. L'écran le
              rend visible au moment du choix, pas dans un rapport plus tard. */}
          {ninNonVerifie && (
            <p className="acte-avis">
              Ce numéro sera consigné comme <strong>non vérifié</strong> : personne n'a vu de pièce.
              L'acte reste valable, et il portera cette réserve.
            </p>
          )}
          <div className="actions">
            <button type="button" className="bouton discret" onClick={() => setEtape(0)}>Retour</button>
            <button type="button" className="bouton primaire" disabled={!acquereurPret}
                    onClick={() => setEtape(2)}>Continuer</button>
            {!acquereurPret && <span className="aide">Un acquéreur doit être désigné.</span>}
          </div>
        </div>
      )}

      {etape === 2 && (
        <div className="acte-corps">
          <h3>Ce qui sera écrit</h3>
          <dl className="acte-relecture">
            <div><dt>Acquéreur</dt><dd>{acquereur?.denomination ?? '—'}
              {acquereur?.identite_verifiee && <span className="jeton vert">identité vérifiée</span>}</dd></div>
            <div><dt>NIN</dt><dd>{acqNin.trim() || <em>non renseigné</em>}
              {acqNin.trim() && <span className={`jeton ${ninNonVerifie ? '' : 'vert'}`}>
                {ninNonVerifie ? 'non vérifié' : 'vérifié'}</span>}</dd></div>
            <div><dt>Ancrage</dt><dd>{lat}, {lon}</dd></div>
            <div><dt>Parcelle</dt><dd>{parcelle.trim() || <em>aucune au registre</em>}</dd></div>
            <div><dt>Désignation</dt><dd>
              {[district && `district ${district}`, secteur && `secteur ${secteur}`, lot && `lot ${lot}`]
                .filter(Boolean).join(' · ') || <em>non désigné</em>}</dd></div>
            <div><dt>Dimensions</dt><dd>
              {superficie ? `${superficie} m²` : <em>non mesurées</em>}
              {longueur && largeur ? ` (${longueur} × ${largeur} m)` : ''}</dd></div>
          </dl>

          <label>Motif
            <input value={motif} onChange={(e) => setMotif(e.target.value)} required minLength={3}
                   placeholder="cession constatée au bureau de la commune" />
          </label>

          {/* ⚠️ CE QUE L'ACTE NE PROUVE PAS SE DIT AVANT DE LE DRESSER, pas
              seulement en tête de la liste. C'est le moment où l'agent engage
              la commune. */}
          <p className="acte-mention">
            <strong>Une attestation de cession NE VAUT PAS TITRE.</strong> Elle constate une
            cession ; elle ne garantit pas la propriété. Le titre foncier est délivré par la
            Conservation Foncière.
          </p>
          <p className="acte-mention">
            <strong>L'ancrage et la commune deviennent IMMUABLES à la création.</strong> Une
            erreur sur l'un des deux s'annule et se recommence — cela se voit, là où une
            correction silencieuse ne se voit pas.
          </p>

          <div className="actions">
            <button type="button" className="bouton discret" onClick={() => setEtape(1)}>Retour</button>
            <button type="button" className="bouton primaire"
                    disabled={envoi || motif.trim().length < 3 || !terrainPret || !acquereurPret}
                    onClick={() => void dresser()}>
              {envoi ? 'Rédaction…' : "Dresser l'acte"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * Choisir une personne DÉJÀ connue du registre.
 * ⚠️ La recherche nominative exige la sensibilité 2 et elle est JOURNALISÉE,
 * y compris quand elle ne rend rien : une recherche infructueuse sur le nom
 * d'un opposant politique est exactement ce qu'un registre foncier doit
 * pouvoir établir a posteriori. L'écran le dit, il ne le cache pas.
 */
function ChoisirTitulaire({ libelle, choisi, onChoisir, aide }: {
  libelle: string; choisi: TitulaireResume | null;
  onChoisir: (t: TitulaireResume | null) => void; aide?: string;
}) {
  const [q, setQ] = useState('');
  const [resultats, setResultats] = useState<TitulaireResume[] | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const chercher = async () => {
    if (q.trim().length < 2) return;
    try {
      const r = await api.foncier.chercherTitulaires(q.trim());
      setResultats(r.titulaires); setMsg(r.message ?? null);
    } catch (e) {
      setMsg(e instanceof ErreurApi ? e.message : String((e as Error)?.message ?? e));
      setResultats([]);
    }
  };

  return (
    <div className="bloc-interne">
      <div className="filtres">
        <label>{libelle}
          <input value={choisi ? choisi.denomination : q}
                 onChange={(e) => { onChoisir(null); setQ(e.target.value); }}
                 placeholder="nom de la personne" />
        </label>
        {!choisi && <button type="button" className="bouton discret" onClick={() => void chercher()}>Chercher</button>}
        {choisi && <button type="button" className="bouton discret" onClick={() => { onChoisir(null); setQ(''); setResultats(null); }}>Changer</button>}
      </div>
      {aide && !choisi && <p className="mention">{aide}</p>}
      {msg && <p className="mention">{msg}</p>}
      {!choisi && resultats && resultats.length > 0 && (
        <ul className="liste-choix">
          {resultats.map((t) => (
            <li key={t.id}>
              <button type="button" className="lien" onClick={() => { onChoisir(t); setResultats(null); }}>
                {t.denomination}
              </button>
              <em className="mention"> · {t.type.replace(/_/g, ' ')}
                {t.nin_rattache ? ' · NIN rattaché' : ''}
                {t.identite_verifiee ? ' · identité vérifiée' : ''}
                {' · '}{t.droits_actifs} droit(s) actif(s)</em>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * L'ACTE OUVERT — le parcours complet, du brouillon au papier remis
 * ═══════════════════════════════════════════════════════════════════════════ */

/**
 * ⚠️ CE QUI MANQUE EST EN TÊTE, ET NOMMÉ. Un agent qui voit « acte incomplet »
 * sans savoir quoi corriger convoque les parties une seconde fois. La liste
 * vient de `fon.attestation_manques` — la même que le déclencheur de délivrance
 * appelle : l'écran annonce donc exactement ce que la base refusera.
 */
function ActeOuvert({ fiche, reglages, chargement, erreur, avis, onFermer, onOuvrirParcelle, onAgir }: {
  fiche: FicheAttestation;
  reglages: ReglagesAttestation | null;
  chargement: boolean; erreur: string | null; avis: string | null;
  onFermer: () => void;
  onOuvrirParcelle: (id: string) => void;
  onAgir: (action: () => Promise<{ message?: string }>) => Promise<void>;
}) {
  const a = fiche.attestation;
  const scelle = a.statut === 'delivree' || a.statut === 'annulee';
  const [motif, setMotif] = useState('');
  const [partie, setPartie] = useState<{
    titulaire: TitulaireResume | null; role: RoleAttestation;
    agitPour: string; pieceType: string; pieceNumero: string;
    nin: string; ninSource: string;
  }>({ titulaire: null, role: 'cedant', agitPour: '', pieceType: '', pieceNumero: '',
       nin: '', ninSource: 'carte_cedeao' });
  const [signature, setSignature] = useState<Record<string, { doc: string; mode: string; lieu: string }>>({});
  const [pieces, setPieces] = useState({ photoCedant: '', photoAcquereur: '', croquisTerrain: '', photoTerrain: '', preuvePaiement: '' });
  const [decl, setDecl] = useState({
    riverainNord: a.riverains.nord ?? '', riverainSud: a.riverains.sud ?? '',
    riverainEst: a.riverains.est ?? '', riverainOuest: a.riverains.ouest ?? '',
    originePropriete: (a.origine.propriete ?? '') as string,
    originePrecision: a.origine.precision ?? '',
    detenuDepuis: a.origine.detenuDepuis ? String(a.origine.detenuDepuis).slice(0, 10) : '',
    aTitreOnereux: a.transaction.aTitreOnereux, prixDeclare: a.transaction.prixDeclare ?? '',
    devise: a.transaction.devise ?? 'GNF', modePaiement: a.transaction.modePaiement ?? '',
    declareSansLitige: a.declarations.sansLitige,
    declareSansHypotheque: a.declarations.sansHypotheque,
    declareSansVenteAnterieure: a.declarations.sansVenteAnterieure,
    chargesEtServitudes: a.declarations.chargesEtServitudes ?? '',
    registreCommunalNumero: a.registreCommunal.numero ?? '',
    registreCommunalPage: a.registreCommunal.page ?? '',
  });
  const [affichage, setAffichage] = useState({ du: a.affichage.du ?? '', au: a.affichage.au ?? '' });
  const [opposition, setOpposition] = useState({ opposant: '', objet: '' });
  const [requisition, setRequisition] = useState<RequisitionDepuisActe | null>(null);
  /**
   * ⚠️ LES HUIT CHAMPS QUE LE SCHÉMA D'ÉTAT EXIGE, pas les quatre que j'avais
   * d'abord prévus. `GET /requisition` les ÉNUMÈRE (il les lit du schéma) :
   * l'écran doit les collecter tous, sinon l'agent convoque le requérant pour
   * recevoir un 400 sur un champ dont personne ne lui a parlé.
   */
  const [etatCivil, setEtatCivil] = useState({
    nom: '', prenoms: '', dateNaissance: '', lieuNaissance: '',
    nationalite: 'Guinéenne', profession: '',
    pieceType: 'carte_electeur', pieceNumero: '', telephone: '',
    region: '', prefecture: '', commune: '',
    engagement: false,
  });

  // ⚠️ On demande au SERVICE ce que l'acte permet, on ne le déduit pas ici :
  // « délivré » ne suffit pas — il faut une parcelle, pas de feuille ouverte,
  // pas de dossier vivant, pas de mutation en cours.
  useEffect(() => {
    if (a.statut !== 'delivree') { setRequisition(null); return; }
    let vivant = true;
    void api.attestations.requisition(a.reference)
      .then((r) => { if (vivant) setRequisition(r); })
      .catch(() => { if (vivant) setRequisition(null); });
    return () => { vivant = false; };
  }, [a.reference, a.statut]);

  /** ⚠️ TROIS ÉTATS, PAS DEUX : `null` = la question n'a pas été posée. Un
   *  booléen à deux états ferait passer un acte non instruit pour un acte sain. */
  const troisEtats = (valeur: boolean | null, poser: (v: boolean | null) => void, libelle: string) => (
    <label>{libelle}
      <select value={valeur === null ? '' : String(valeur)}
              disabled={scelle}
              onChange={(e) => poser(e.target.value === '' ? null : e.target.value === 'true')}>
        <option value="">Question non posée</option>
        <option value="true">Non — le cédant déclare qu'il n'y en a pas</option>
        <option value="false">OUI — le cédant en déclare un</option>
      </select>
    </label>
  );

  return (
    <div className="tableau-bord">
      <div className="entete-scene">
        <button className="bouton discret" onClick={onFermer}>← Toutes les attestations</button>
        <h2><Icone nom="attestation" taille={20} /> <span className="mono">{a.reference}</span></h2>
        <p className="mention">
          {LIBELLE_STATUT_ATTESTATION[a.statut as StatutAttestation] ?? a.statut} ·{' '}
          <b>{a.commune}</b> · {a.region} · {a.prefecture} · {a.sousPrefecture}
          {a.district && <> · district {a.district}</>}{a.secteur && <> · secteur {a.secteur}</>}
          {' · '}ancrage {a.ancrage.lat}, {a.ancrage.lon}
          {a.parcelle && <> · <button className="lien" onClick={() => onOuvrirParcelle(a.parcelle!)}>{a.parcelle}</button></>}
        </p>
        <p className="mention">{reglages?.avertissement}</p>
      </div>

      {erreur && <p className="erreur">{erreur}</p>}
      {avis && <p className="succes">{avis}</p>}
      {chargement && <p className="attente">Écriture au registre communal…</p>}

      {a.statut === 'annulee' && (
        <p className="erreur">
          Acte ANNULÉ le {String(a.annuleeLe).slice(0, 10)} — {a.motifAnnulation}.
          Le document délivré a été révoqué : le QR imprimé sur le papier remis dit « révoqué ».
        </p>
      )}

      {a.statut !== 'delivree' && a.statut !== 'annulee' && (
        <section className={`bloc ${fiche.manques.length ? 'bloc-alerte' : ''}`}>
          <h3>
            {fiche.manques.length
              ? <><Icone nom="alerte" taille={16} /> Ce qui manque pour délivrer — {fiche.manques.length}</>
              : <><Icone nom="valide" taille={16} /> Complet : cet acte peut être délivré</>}
          </h3>
          {fiche.manques.length > 0
            ? <ul className="liste-manques">{fiche.manques.map((m, i) => <li key={i}>{m}</li>)}</ul>
            : <p className="mention">
                Toutes les exigences de la base sont satisfaites. Le contrôle sera REJOUÉ au moment
                de la délivrance : si un manque surgit entre-temps, rien ne sortira.
              </p>}
        </section>
      )}

      {/* ── LES PARTIES ─────────────────────────────────────────────────── */}
      <section className="bloc">
        <h3>Les parties</h3>
        <p className="mention">
          Acquéreur : <b>{a.acquereur.denomination}</b> ({a.acquereur.type.replace(/_/g, ' ')}).
          Chaque partie est une personne connue du registre : un témoin anonyme ne témoigne de rien.
          Signature <b>ou</b> empreinte digitale — les deux valent, l'absence des deux non.
        </p>
        {fiche.parties.length === 0
          ? <p className="indetermine">Aucune partie inscrite. Une cession se constate entre un cédant et un acquéreur, devant témoins.</p>
          : (
            <table className="tableau">
              <thead><tr><th>Qualité</th><th>Nom</th><th>Identité nationale</th><th>Pièce</th>
                <th>Signé</th><th>Le</th><th>Lieu</th>{!scelle && <th>Action</th>}</tr></thead>
              <tbody>
                {fiche.parties.map((p) => {
                  const sig = signature[p.id] ?? { doc: '', mode: 'signature', lieu: '' };
                  const aSigne = p.signe_le !== null && (p.a_signature || p.a_empreinte);
                  return (
                    <tr key={p.id}>
                      <td>{LIBELLE_ROLE_ATTESTATION[p.role as RoleAttestation] ?? p.role.replace(/_/g, ' ')}
                        {p.agit_pour && <em className="mention"><br />{p.agit_pour}</em>}</td>
                      <td>{p.denomination}<em className="mention"><br />{p.titulaire_type.replace(/_/g, ' ')}</em></td>
                      {/* ⚠️ DIRE PAR QUOI LA PERSONNE EST IDENTIFIÉE, pas seulement
                          qu'elle l'est : Mowibo ID et NIN n'ont pas la même force,
                          et « ni l'un ni l'autre » est un manque nommé. */}
                      {/* ⚠️ LA MÊME RÉDACTION QUE L'ACTE IMPRIMÉ (`libelleNin`) :
                          si l'écran et le papier se formulaient séparément, c'est
                          le papier qui ferait foi et l'écran qui mentirait. */}
                      <td className="mono">{
                        p.nin_rattache || p.mowibo_mid || p.identite_verifiee
                          ? libelleNin(p)
                          : <span className="erreur-inline">aucune — acte non délivrable</span>
                      }</td>
                      <td className="mono">{p.piece_identite_type
                        ? `${LIBELLE_PIECE[p.piece_identite_type as PieceIdentite] ?? p.piece_identite_type}${p.piece_identite_numero ? ` n° ${p.piece_identite_numero}` : ''}`
                        : '—'}</td>
                      <td>{p.a_signature ? 'Signature' : p.a_empreinte ? 'Empreinte' : '—'}</td>
                      <td>{p.signe_le ? String(p.signe_le).slice(0, 10) : '—'}</td>
                      <td>{p.signe_lieu ?? '—'}</td>
                      {!scelle && (
                        <td>
                          {aSigne ? <em className="mention">recueillie</em> : (
                            <div className="filtres serre">
                              <select value={sig.mode}
                                      onChange={(e) => setSignature({ ...signature, [p.id]: { ...sig, mode: e.target.value } })}>
                                <option value="signature">Signature</option>
                                <option value="empreinte">Empreinte digitale</option>
                              </select>
                              <input value={sig.doc} placeholder="GN-DOC-… (pièce déposée)"
                                     onChange={(e) => setSignature({ ...signature, [p.id]: { ...sig, doc: e.target.value } })} />
                              <input value={sig.lieu} placeholder="lieu"
                                     onChange={(e) => setSignature({ ...signature, [p.id]: { ...sig, lieu: e.target.value } })} />
                              <button className="bouton discret" disabled={!sig.doc}
                                      onClick={() => void onAgir(() => api.attestations.signer(a.reference, p.id, {
                                        signatureDocumentId: sig.mode === 'signature' ? sig.doc : undefined,
                                        empreinteDigitaleDocumentId: sig.mode === 'empreinte' ? sig.doc : undefined,
                                        lieu: sig.lieu || undefined,
                                      }))}>Recueillir</button>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

        {!scelle && (
          <form onSubmit={(e) => {
            e.preventDefault();
            void onAgir(() => api.attestations.ajouterPartie(a.reference, {
              titulaireId: partie.titulaire?.id, role: partie.role,
              agitPour: partie.agitPour || undefined,
              pieceIdentiteType: partie.pieceType || undefined,
              pieceIdentiteNumero: partie.pieceNumero || undefined,
              nin: partie.nin.trim() || undefined,
              ninSource: partie.nin.trim() ? partie.ninSource : undefined,
            }));
          }}>
            <ChoisirTitulaire libelle="Ajouter une partie" choisi={partie.titulaire}
                              onChoisir={(t) => setPartie({ ...partie, titulaire: t })} />
            <div className="filtres">
              <label>Qualité
                <select value={partie.role} onChange={(e) => setPartie({ ...partie, role: e.target.value as RoleAttestation })}>
                  {(reglages?.roles ?? []).map((r) => (
                    <option key={r} value={r}>{LIBELLE_ROLE_ATTESTATION[r as RoleAttestation] ?? r}</option>
                  ))}
                </select>
              </label>
              <label>Agit pour (facultatif)
                <input value={partie.agitPour} placeholder="au nom de la famille …"
                       onChange={(e) => setPartie({ ...partie, agitPour: e.target.value })} />
              </label>
              <label>Pièce d'identité
                <select value={partie.pieceType} onChange={(e) => setPartie({ ...partie, pieceType: e.target.value })}>
                  <option value="">— non déclarée —</option>
                  {(reglages?.piecesIdentite ?? []).map((p) => (
                    <option key={p} value={p}>{LIBELLE_PIECE[p as PieceIdentite] ?? p}</option>
                  ))}
                </select>
              </label>
              <label>N° de la pièce
                <input value={partie.pieceNumero} onChange={(e) => setPartie({ ...partie, pieceNumero: e.target.value })} />
              </label>
              {/* ⚠️ LE NUMÉRO NE SERA PAS CONSERVÉ. Il est validé (clé modulo 10),
                  décodé et haché par le service ; la fiche ne garde qu'une
                  empreinte et les quatre derniers chiffres. Le dire ici évite
                  qu'un agent croie le registre dépositaire des identités. */}
              <label>Identité nationale (NIN)
                <input value={partie.nin} inputMode="numeric" maxLength={20}
                       placeholder="15 chiffres"
                       onChange={(e) => setPartie({ ...partie, nin: e.target.value })} />
              </label>
              {partie.nin.trim() && (
                <label>Source du NIN
                  <select value={partie.ninSource}
                          onChange={(e) => setPartie({ ...partie, ninSource: e.target.value })}>
                    <option value="carte_cedeao">Carte CEDEAO présentée — vérifié</option>
                    <option value="mowibo_id">Mowibo ID — vérifié</option>
                    <option value="declaration">Déclaré par l'intéressé — NON vérifié</option>
                  </select>
                </label>
              )}
              <button className="bouton" disabled={!partie.titulaire}>Inscrire la partie</button>
            </div>
            <p className="mention">
              Le NIN est saisi ici et <b>n'est pas conservé</b> : le service le valide, en tire ce
              qu'il contient (sexe, date de naissance, lieu) puis le hache. La fiche garde une
              empreinte et les quatre derniers chiffres — c'est ce qui s'imprimera sur l'acte.
              « Déclaré » n'est pas « vérifié » : les deux n'ont pas la même valeur en droit.
            </p>
          </form>
        )}
      </section>

      {/* ── LES PIÈCES ──────────────────────────────────────────────────── */}
      <section className="bloc">
        <h3>Pièces du terrain et des personnes</h3>
        <p className="mention">
          Le plan à l'échelle est CALCULÉ depuis le registre cadastral, il ne se dépose pas. Ce qui
          se dépose est ce qu'aucun calcul ne remplace : le croquis levé à la main, les photos du
          sol et des personnes. Déposez d'abord la pièce dans <b>Documents</b>, puis citez son
          identifiant ici.
        </p>
        <dl className="paires">
          <dt>Photo du cédant</dt><dd>{a.pieces.photoCedant ? 'déposée' : <em className="mention">absente</em>}</dd>
          <dt>Photo de l'acquéreur</dt><dd>{a.pieces.photoAcquereur ? 'déposée' : <em className="mention">absente</em>}</dd>
          <dt>Croquis de terrain</dt><dd>{a.pieces.croquisTerrain ? 'déposé' : <em className="mention">absent</em>}</dd>
          <dt>Photo du terrain</dt><dd>{a.pieces.photoTerrain ? 'déposée' : <em className="mention">absente</em>}</dd>
          <dt>Tracé levé</dt><dd>{a.contourSurfaceM2 !== null ? `${a.contourSurfaceM2} m² mesurés` : <em className="mention">aucun contour</em>}</dd>
        </dl>
        {!scelle && (
          <form className="filtres" onSubmit={(e) => {
            e.preventDefault();
            void onAgir(() => api.attestations.pieces(a.reference, {
              photoCedant: pieces.photoCedant || undefined,
              photoAcquereur: pieces.photoAcquereur || undefined,
              croquisTerrain: pieces.croquisTerrain || undefined,
              photoTerrain: pieces.photoTerrain || undefined,
              preuvePaiement: pieces.preuvePaiement || undefined,
            }));
          }}>
            <label>Photo cédant<input value={pieces.photoCedant} placeholder="GN-DOC-…"
                   onChange={(e) => setPieces({ ...pieces, photoCedant: e.target.value })} /></label>
            <label>Photo acquéreur<input value={pieces.photoAcquereur} placeholder="GN-DOC-…"
                   onChange={(e) => setPieces({ ...pieces, photoAcquereur: e.target.value })} /></label>
            <label>Croquis<input value={pieces.croquisTerrain} placeholder="GN-DOC-…"
                   onChange={(e) => setPieces({ ...pieces, croquisTerrain: e.target.value })} /></label>
            <label>Photo terrain<input value={pieces.photoTerrain} placeholder="GN-DOC-…"
                   onChange={(e) => setPieces({ ...pieces, photoTerrain: e.target.value })} /></label>
            <label>Preuve de paiement<input value={pieces.preuvePaiement} placeholder="GN-DOC-…"
                   onChange={(e) => setPieces({ ...pieces, preuvePaiement: e.target.value })} /></label>
            <button className="bouton">Rattacher</button>
          </form>
        )}
      </section>

      {/* ── LE FOND DE L'ACTE ───────────────────────────────────────────── */}
      <section className="bloc">
        <h3>Riverains, origine et déclarations</h3>
        <form onSubmit={(e) => {
          e.preventDefault();
          void onAgir(() => api.attestations.completer(a.reference, {
            riverainNord: decl.riverainNord || null, riverainSud: decl.riverainSud || null,
            riverainEst: decl.riverainEst || null, riverainOuest: decl.riverainOuest || null,
            originePropriete: decl.originePropriete || null,
            originePrecision: decl.originePrecision || null,
            detenuDepuis: decl.detenuDepuis || null,
            aTitreOnereux: decl.aTitreOnereux,
            prixDeclare: decl.prixDeclare === '' ? null : Number(decl.prixDeclare),
            devise: decl.devise || null, modePaiement: decl.modePaiement || null,
            declareSansLitige: decl.declareSansLitige,
            declareSansHypotheque: decl.declareSansHypotheque,
            declareSansVenteAnterieure: decl.declareSansVenteAnterieure,
            chargesEtServitudes: decl.chargesEtServitudes || null,
            registreCommunalNumero: decl.registreCommunalNumero || null,
            registreCommunalPage: decl.registreCommunalPage || null,
            motif: 'Complément de l\'acte au bureau de la commune',
          }));
        }}>
          <div className="filtres">
            <label>Riverain Nord<input value={decl.riverainNord} disabled={scelle}
                   onChange={(e) => setDecl({ ...decl, riverainNord: e.target.value })} /></label>
            <label>Riverain Sud<input value={decl.riverainSud} disabled={scelle}
                   onChange={(e) => setDecl({ ...decl, riverainSud: e.target.value })} /></label>
            <label>Riverain Est<input value={decl.riverainEst} disabled={scelle}
                   onChange={(e) => setDecl({ ...decl, riverainEst: e.target.value })} /></label>
            <label>Riverain Ouest<input value={decl.riverainOuest} disabled={scelle}
                   onChange={(e) => setDecl({ ...decl, riverainOuest: e.target.value })} /></label>
          </div>
          <div className="filtres">
            <label>Origine de la propriété du cédant
              <select value={decl.originePropriete} disabled={scelle}
                      onChange={(e) => setDecl({ ...decl, originePropriete: e.target.value })}>
                <option value="">— non déclarée —</option>
                {(reglages?.origines ?? []).map((o) => (
                  <option key={o} value={o}>{LIBELLE_ORIGINE[o as OriginePropriete] ?? o}</option>
                ))}
              </select>
            </label>
            <label>Détenu depuis
              <input type="date" value={decl.detenuDepuis} disabled={scelle}
                     onChange={(e) => setDecl({ ...decl, detenuDepuis: e.target.value })} />
            </label>
            <label>Précision de l'origine
              <input value={decl.originePrecision} disabled={scelle}
                     onChange={(e) => setDecl({ ...decl, originePrecision: e.target.value })} />
            </label>
          </div>
          <div className="filtres">
            {troisEtats(decl.aTitreOnereux, (v) => setDecl({ ...decl, aTitreOnereux: v }), 'Cession à titre onéreux')}
            <label>Prix déclaré
              <input value={String(decl.prixDeclare)} disabled={scelle} inputMode="decimal"
                     onChange={(e) => setDecl({ ...decl, prixDeclare: e.target.value })} />
            </label>
            <label>Devise
              <input value={decl.devise} disabled={scelle} maxLength={3}
                     onChange={(e) => setDecl({ ...decl, devise: e.target.value.toUpperCase() })} />
            </label>
            <label>Mode de paiement
              <input value={decl.modePaiement} disabled={scelle}
                     onChange={(e) => setDecl({ ...decl, modePaiement: e.target.value })} />
            </label>
          </div>
          <div className="filtres">
            {troisEtats(decl.declareSansLitige, (v) => setDecl({ ...decl, declareSansLitige: v }), 'Litige connu sur le fonds')}
            {troisEtats(decl.declareSansHypotheque, (v) => setDecl({ ...decl, declareSansHypotheque: v }), 'Hypothèque ou sûreté')}
            {troisEtats(decl.declareSansVenteAnterieure, (v) => setDecl({ ...decl, declareSansVenteAnterieure: v }), 'Vente antérieure du fonds')}
          </div>
          <div className="filtres">
            <label>Charges et servitudes
              <input value={decl.chargesEtServitudes} disabled={scelle}
                     onChange={(e) => setDecl({ ...decl, chargesEtServitudes: e.target.value })} />
            </label>
            <label>Registre communal n°
              <input value={decl.registreCommunalNumero} disabled={scelle}
                     onChange={(e) => setDecl({ ...decl, registreCommunalNumero: e.target.value })} />
            </label>
            <label>Page
              <input value={decl.registreCommunalPage} disabled={scelle}
                     onChange={(e) => setDecl({ ...decl, registreCommunalPage: e.target.value })} />
            </label>
            {!scelle && <button className="bouton">Enregistrer</button>}
          </div>
          <p className="mention">
            « Question non posée » n'est pas « aucun litige » : les confondre ferait passer un acte
            non instruit pour un acte sain. Une cession sur un terrain grevé PEUT être constatée —
            à condition que la charge soit écrite. C'est le silence qu'on refuse, jamais l'aveu.
          </p>
        </form>
      </section>

      {/* ── AFFICHAGE ET OPPOSITION ─────────────────────────────────────── */}
      <section className="bloc">
        <h3>Affichage à la commune</h3>
        <p className="mention">
          Un délai que personne n'a pu voir courir ne s'oppose à personne. Sans affichage, une
          cession se constate entre quatre personnes et surprend le voisin qui avait un droit à
          faire valoir.
          {a.affichage.du && <> Affiché du {String(a.affichage.du).slice(0, 10)} au {String(a.affichage.au).slice(0, 10)}.</>}
        </p>
        {!scelle && (
          <form className="filtres" onSubmit={(e) => {
            e.preventDefault();
            void onAgir(() => api.attestations.affichage(a.reference, affichage));
          }}>
            <label>Du<input type="date" value={affichage.du} required
                   onChange={(e) => setAffichage({ ...affichage, du: e.target.value })} /></label>
            <label>Au<input type="date" value={affichage.au} required
                   onChange={(e) => setAffichage({ ...affichage, au: e.target.value })} /></label>
            <button className="bouton">Ouvrir l'affichage</button>
          </form>
        )}
        {a.affichage.oppositionRecue && (
          <p className="erreur">
            UNE OPPOSITION a été reçue. Elle est consignée sur l'acte, IMPRIMÉE sur le papier, et
            suit le dossier jusqu'au titre : un acquéreur doit savoir ce qui pèse sur son terrain.
          </p>
        )}
        {!scelle && (
          <form className="filtres" onSubmit={(e) => {
            e.preventDefault();
            void onAgir(() => api.attestations.opposition(a.reference, opposition));
          }}>
            <label>Opposant
              <input value={opposition.opposant} required minLength={3}
                     onChange={(e) => setOpposition({ ...opposition, opposant: e.target.value })} />
            </label>
            <label>Objet de l'opposition (10 caractères minimum)
              <input value={opposition.objet} required minLength={10}
                     onChange={(e) => setOpposition({ ...opposition, objet: e.target.value })} />
            </label>
            <button className="bouton discret">Consigner une opposition</button>
            {/* ⚠️ CONSIGNER N'EST PAS BLOQUER, et le dire évite qu'un agent croie
                l'acte suspendu. Une attestation CONSTATE ce que les parties
                déclarent devant témoins : l'empêcher au premier tiers qui
                s'oppose donnerait à n'importe qui un droit de veto sur l'acte
                d'un autre, sans juge et sans preuve. C'est à l'IMMATRICULATION
                que l'opposition bloque, et c'est là que l'État tranche. */}
            <p className="mention">
              Consigner n'empêche pas de délivrer : l'opposition est écrite, imprimée et suit le
              dossier. C'est à l'immatriculation qu'elle bloque — là où l'État tranche.
            </p>
          </form>
        )}
      </section>

      {/* ── LE PLAN FONCIER COMMUNAL ────────────────────────────────────── */}
      {fiche.inscriptionPlanFoncier && (
        <section className="bloc">
          <h3>Inscription au plan foncier (Art.3, Art.7)</h3>
          <p className="mention">
            Inscrit au plan foncier de {fiche.inscriptionPlanFoncier.collectivite} le{' '}
            {String(fiche.inscriptionPlanFoncier.inscrite_le).slice(0, 10)}
            {fiche.inscriptionPlanFoncier.numero_ordre && <> sous le n° {fiche.inscriptionPlanFoncier.numero_ordre}</>}
            {fiche.inscriptionPlanFoncier.radiee_le && <> — RADIÉE le {String(fiche.inscriptionPlanFoncier.radiee_le).slice(0, 10)}</>}.
          </p>
        </section>
      )}

      {/* ── LES DEUX GESTES QUI ENGAGENT ────────────────────────────────── */}
      {!scelle && (
        <section className="bloc">
          <h3>Signer, puis délivrer</h3>
          <p className="mention">
            Signer n'est pas délivrer. La signature est le moment où les parties s'engagent devant
            l'autorité ; la délivrance est le moment où le papier SORT de la mairie. Entre les
            deux, la commune vérifie, enregistre à son registre et affiche.
          </p>
          <div className="filtres">
            {a.statut === 'brouillon' && (
              <button className="bouton" onClick={() => void onAgir(() => api.attestations.acterSignature(a.reference))}>
                Acter la signature de l'acte
              </button>
            )}
            <button className="bouton" disabled={!fiche.delivrable}
                    title={fiche.delivrable ? undefined : `${fiche.manques.length} point(s) manquant(s)`}
                    onClick={() => void onAgir(() => api.attestations.delivrer(a.reference))}>
              <Icone nom="imprimer" taille={15} /> Délivrer — produire le papier
            </button>
          </div>
        </section>
      )}

      {a.statut === 'delivree' && (
        <section className="bloc">
          <h3><Icone nom="valide" taille={16} /> Acte délivré</h3>
          <p className="mention">
            Délivré le {String(a.delivreeLe).slice(0, 10)}. Son contenu est SCELLÉ : le papier est
            entre les mains des parties, et le modifier ferait diverger le registre et l'original
            remis. Une erreur découverte après coup s'ANNULE, avec motif, et on dresse un autre acte.
          </p>
          {a.documentId && (
            <p><a className="lien" href={`/documents/${a.documentId}/contenu`} target="_blank" rel="noreferrer">
              Télécharger le PDF délivré
            </a></p>
          )}
        </section>
      )}

      {/* ── L'ACTE MÈNE AU TITRE (Art.3) ────────────────────────────────── */}
      {requisition && (
        <section className={`bloc ${requisition.requerable ? '' : 'bloc-alerte'}`}>
          <h3><Icone nom="livre" taille={16} /> Requérir l'immatriculation</h3>
          {/* La phrase vient du SERVICE : c'est lui qui sait ce qui empêche. */}
          <p className="mention">{requisition.lecture}</p>

          {!requisition.requerable && (
            <ul className="liste-manques">
              {requisition.empechements.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          )}

          {requisition.requerable && (
            <>
              <h4 style={{ margin: '14px 0 4px', fontSize: 13 }}>Ce que l'acte fournit</h4>
              <dl className="paires">
                <dt>Immeuble</dt>
                <dd>{String(requisition.fourni.immeuble.identifiantParcelle ?? '—')} ·{' '}
                  {String(requisition.fourni.immeuble.region)} /{' '}
                  {String(requisition.fourni.immeuble.prefecture)} /{' '}
                  {String(requisition.fourni.immeuble.communeOuSousPrefecture)}</dd>
                <dt>Origine de détention</dt><dd>{requisition.fourni.origineDetention}</dd>
                <dt>Détail</dt><dd>{requisition.fourni.origineDetail}</dd>
                <dt>Riverains</dt>
                <dd>{requisition.fourni.riverains.length
                  ? requisition.fourni.riverains.map((r) => `${r.cote} : ${r.nom}`).join(' · ')
                  : <em className="mention">aucun déclaré</em>}</dd>
                <dt>Pièces</dt><dd>{requisition.fourni.piecesJointes.join(' · ')}</dd>
              </dl>

              {/* ⚠️ CE QUE LE REGISTRE NE SAIT PAS EST AFFICHÉ AVANT LE
                  FORMULAIRE, pas après : l'agent doit savoir ce qu'il va devoir
                  demander à la personne AVANT de la faire asseoir. */}
              <h4 style={{ margin: '14px 0 4px', fontSize: 13 }}>
                Ce qu'il faut demander au requérant — {requisition.aDemander.length}
              </h4>
              <ul className="liste-manques">
                {requisition.aDemander.map((m, i) => <li key={i}>{m}</li>)}
              </ul>

              <form onSubmit={(e) => {
                e.preventDefault();
                void onAgir(() => api.attestations.requerir(a.reference, {
                  demandeur: {
                    qualite: a.acquereur.type === 'personne_morale' ? 'personne_morale' : 'personne_physique',
                    agitPour: 'soi_meme',
                    personne: {
                      nom: etatCivil.nom, prenoms: etatCivil.prenoms,
                      dateNaissance: etatCivil.dateNaissance,
                      lieuNaissance: etatCivil.lieuNaissance,
                      nationalite: etatCivil.nationalite || 'Guinéenne',
                      ...(etatCivil.profession ? { profession: etatCivil.profession } : {}),
                      pieceType: etatCivil.pieceType, pieceNumero: etatCivil.pieceNumero,
                      telephone: etatCivil.telephone,
                      adresse: {
                        region: etatCivil.region || a.region,
                        prefecture: etatCivil.prefecture || a.prefecture,
                        communeOuSousPrefecture: etatCivil.commune || a.sousPrefecture,
                      },
                    },
                  },
                  engagementConfidentialite: etatCivil.engagement,
                }));
              }}>
                <div className="filtres">
                  <label>Nom (état civil)
                    <input value={etatCivil.nom} required
                           onChange={(ev) => setEtatCivil({ ...etatCivil, nom: ev.target.value })} />
                  </label>
                  <label>Prénoms
                    <input value={etatCivil.prenoms} required
                           onChange={(ev) => setEtatCivil({ ...etatCivil, prenoms: ev.target.value })} />
                  </label>
                  <label>Date de naissance
                    <input type="date" value={etatCivil.dateNaissance} required
                           onChange={(ev) => setEtatCivil({ ...etatCivil, dateNaissance: ev.target.value })} />
                  </label>
                  <label>Lieu de naissance
                    <input value={etatCivil.lieuNaissance} required
                           onChange={(ev) => setEtatCivil({ ...etatCivil, lieuNaissance: ev.target.value })} />
                  </label>
                  <label>Nationalité
                    <input value={etatCivil.nationalite}
                           onChange={(ev) => setEtatCivil({ ...etatCivil, nationalite: ev.target.value })} />
                  </label>
                  <label>Profession
                    <input value={etatCivil.profession}
                           onChange={(ev) => setEtatCivil({ ...etatCivil, profession: ev.target.value })} />
                  </label>
                </div>
                <div className="filtres">
                  <label>Pièce d'identité présentée
                    <select value={etatCivil.pieceType}
                            onChange={(ev) => setEtatCivil({ ...etatCivil, pieceType: ev.target.value })}>
                      {(reglages?.piecesIdentite ?? []).map((p) => (
                        <option key={p} value={p}>{LIBELLE_PIECE[p as PieceIdentite] ?? p}</option>
                      ))}
                    </select>
                  </label>
                  <label>N° de la pièce
                    <input value={etatCivil.pieceNumero} required
                           onChange={(ev) => setEtatCivil({ ...etatCivil, pieceNumero: ev.target.value })} />
                  </label>
                  <label>Téléphone
                    <input value={etatCivil.telephone} required placeholder="+224…"
                           onChange={(ev) => setEtatCivil({ ...etatCivil, telephone: ev.target.value })} />
                  </label>
                </div>
                {/* ⚠️ L'ADRESSE EST PRÉ-REMPLIE PAR CELLE DE L'ACTE, et reste
                    modifiable : le requérant n'habite pas forcément le terrain
                    qu'il acquiert. Un champ pré-rempli non modifiable ferait
                    déclarer une adresse fausse à chaque acquéreur venu d'ailleurs. */}
                <div className="filtres">
                  <label>Région (adresse du requérant)
                    <input value={etatCivil.region} placeholder={a.region}
                           onChange={(ev) => setEtatCivil({ ...etatCivil, region: ev.target.value })} />
                  </label>
                  <label>Préfecture
                    <input value={etatCivil.prefecture} placeholder={a.prefecture}
                           onChange={(ev) => setEtatCivil({ ...etatCivil, prefecture: ev.target.value })} />
                  </label>
                  <label>Commune ou sous-préfecture
                    <input value={etatCivil.commune} placeholder={a.sousPrefecture}
                           onChange={(ev) => setEtatCivil({ ...etatCivil, commune: ev.target.value })} />
                  </label>
                </div>
                <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', margin: '8px 0' }}>
                  <input type="checkbox" checked={etatCivil.engagement}
                         onChange={(ev) => setEtatCivil({ ...etatCivil, engagement: ev.target.checked })} />
                  <span style={{ fontSize: 12 }}>
                    Le requérant accepte l'engagement de confidentialité.
                    {' '}<b>Cette case se coche devant lui</b> — un engagement coché à sa place
                    n'engage personne.
                  </span>
                </label>
                <button className="bouton" disabled={!etatCivil.engagement}>
                  Déposer la réquisition d'immatriculation
                </button>
                <p className="mention">
                  Le titre ne sera PAS délivré par ce geste : la réquisition ouvre un dossier et
                  part au premier service de son circuit. Restent l'instruction, la publicité
                  foncière et le délai d'opposition — ce sont eux qui rendent l'immatriculation
                  opposable aux tiers.
                </p>
              </form>
            </>
          )}
        </section>
      )}

      {a.statut !== 'annulee' && (
        <section className="bloc bloc-alerte">
          <h3><Icone nom="alerte" taille={16} /> Annuler cet acte</h3>
          <form className="filtres" onSubmit={(e) => {
            e.preventDefault();
            void onAgir(() => api.attestations.annuler(a.reference, motif));
          }}>
            <label>Motif (10 caractères minimum)
              <input value={motif} required minLength={10} onChange={(e) => setMotif(e.target.value)}
                     placeholder="erreur sur l'identité du cédant, constatée au guichet le …" />
            </label>
            <button className="bouton danger">Annuler l'acte</button>
          </form>
          <p className="mention">
            L'annulation RADIE l'inscription au plan foncier et RÉVOQUE le document délivré : le QR
            imprimé sur le papier remis dira « révoqué ». L'acte reste au registre — on annule, on
            n'efface pas.
          </p>
        </section>
      )}
    </div>
  );
}
